<script setup>
import { ref, onMounted } from "vue";
import { CoursesClient } from "@/services/CoursesClient";
import { GradesClient } from "@/services/GradesClient";
import { StudentsClient } from "@/services/StudentsClient";

const courses = ref([]);
const isLoading = ref(true);
const error = ref(null);
const coursesClient = new CoursesClient();
const gradesClient = new GradesClient();
const studentsClient = new StudentsClient();

// Fetch instructor courses and their grades
const fetchCoursesWithGrades = async () => {
  try {
    isLoading.value = true;
    error.value = null;

    // 1. Get instructor courses
    const instructorCourses = await coursesClient.getInstructorCoursesById();
    console.log(instructorCourses);


    // 2. Transform the courses data
    const transformedCourses = instructorCourses.map(course => ({
      id: course.id,
      name: course.titulo,
      label: course.categoria_nombre,
      students: 0, // Will be updated
      isOpen: false,
      studentsList: []
    }));

    // 3. For each course, fetch students and their grades
    const coursesWithStudents = await Promise.all(
      transformedCourses.map(async course => {
        try {
          const students = await studentsClient.getStudentsByCourse(course.id);
          console.log(students);

          return {
            ...course,
            students: students.length,
            studentsList: transformStudentsData(students)
          };
        } catch (err) {
          console.error(`Error fetching students for course ${course.id}:`, err);
          return course;
        }
      })
    );

    courses.value = coursesWithStudents;
  } catch (err) {
    error.value = err.message || "Failed to load courses";
    console.error("Error fetching courses:", err);
  } finally {
    isLoading.value = false;
  }
};

// Transform students data to match component structure
const transformStudentsData = (students) => {
  return students.map(student => ({
    id: student.estudiante_id,
    nombre: student.nombre,
    email: student.email,
    totalGrade: student.nota,
    isOpen: false,
    quizzes: transformQuizzesData(student.quices)
  }));
};


// Transform quizzes data to match component structure
const transformQuizzesData = (quices) => {
  if (!quices) return [];

  return quices.map(quiz => {
    const [correct, total] = quiz.resumen_preguntas.split('/').map(Number);

    const isSubmitted = quiz.calificacion === null ? "No entregado" : "Entregado";

    return {
      id: quiz.id || Math.random().toString(36).substring(2, 9),
      name: quiz.quiz_nombre,
      grade: quiz.calificacion !== null ? Math.round(quiz.calificacion) : "-",
      correct: correct || 0,
      total: total || 0,
      submitted: isSubmitted
    };
  });
};


// Initialize data
onMounted(fetchCoursesWithGrades);

// Existing toggle functions
const toggleCourse = (courseId) => {
  const course = courses.value.find(c => c.id === courseId);
  if (course) course.isOpen = !course.isOpen;
};

const toggleStudent = (courseId, studentId) => {
  const course = courses.value.find(c => c.id === courseId);
  if (course) {
    const student = course.studentsList.find(s => s.id === studentId);
    if (student) student.isOpen = !student.isOpen;
  }
};
</script>

<template>
  <div class="p-3 py-md-4 p-lg-4 container-fluid">
    <!-- Loading state -->
    <div v-if="isLoading" class="text-center py-9 my-9">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Error state -->
    <div v-if="error" class="alert alert-danger">
      Error loading courses: {{ error }}
    </div>

    <!-- Main content -->
    <div v-if="!isLoading && !error" class="mb-4 card">
      <div class="p-3 pb-0 card-header">
        <div class="row align-items-top">
          <!-- Title Column (full width on mobile, 9 cols on desktop) -->
          <div class="col-12 col-md-8 order-1 order-md-1 mb-3 mb-md-0 ">
            <h5 class="mb-1">
              <i class="fas fa-spell-check text-lg pe-2"></i>
              Calificaciones de Estudiantes
            </h5>
          </div>

          <!-- Stats Column (full width on mobile, 3 cols on desktop) -->
          <div class="col-12 col-md-4 order-2 order-md-2">
            <div class="row justify-content-end">
              <!-- Total Students -->
              <div class="col-6  d-flex align-items-center justify-content-end my-2 my-md-1">
                <p class="small mb-0 pe-2">Total Estudiantes</p>
                <span class="badge bg-primary">
                  {{courses.reduce((acc, course) => acc + course.students, 0)}}
                </span>
              </div>

              <!-- Approved Students -->
              <div class="col-6 d-flex align-items-center justify-content-end my-2">
                <p class="small mb-0 pe-2">Aprobados</p>
                <span class="badge bg-success">
                  {{
                    courses.reduce((acc, course) => {
                      return acc + course.studentsList.filter(s => s.totalGrade >= 70).length
                    }, 0)
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="p-1">
          <ul class="text-xs text-gray-600 mb-0">
            <li>Haz clic en cualquier <span class="font-semibold text-blue-600">curso</span> para ver la lista de
              estudiantes</li>
            <li>Selecciona un <span class="font-semibold text-green-600">estudiante</span> para ver sus calificaciones
              detalladas</li>
          </ul>
        </div>
      </div>

      <div class="p-2 p-md-4 pt-0 card-body">
        <div class="accordion" id="gradesAccordion">
          <!-- Course Loop - Wrapped in scrollable container -->
          <div class="course-list-container" style="max-height: 150vh; min-height: 75vh; overflow-y: auto;">
            <div v-for="course in courses" :key="course.id" class="mb-4 card course">
              <div
                class="p-3 card-header cursor-pointer d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center border-bottom"
                @click="toggleCourse(course.id)" :class="course.isOpen ? 'bg-gray-200' : 'bg-gray-100'">
                <div class="w-100 w-md-auto">
                  <span class="badge category-badge">{{ course.label }}</span>
                  <div class="d-flex align-items-center justify-content-between">
                    <h6 class="mb-0">{{ course.name }}</h6>
                    <i class="fas fa-chevron-down transition-all ms-2 d-md-none align-self-end"
                      :class="{ 'rotate-180': course.isOpen }"></i>
                  </div>
                </div>     
                
                
                <!-- Mobile-only stats (shown below course name) -->
                <div class="d-flex d-md-none justify-content-between w-100 mt-2">
                  <div v-if="course.isOpen"  class="text-center">
                    <span class="badge bg-dark text-white">
                      {{ course.students }}
                    </span>
                    <p class="small mb-0">Estudiantes</p>
                  </div>
                  <div v-if="course.isOpen" class="text-center">
                    <span class="badge bg-success text-white">
                      {{course.studentsList.filter(s => s.totalGrade >= 70).length}}
                    </span>
                    <p class="small mb-0">Aprobados</p>
                  </div>
                  <div v-if="course.isOpen" class="text-center">
                    <span class="badge bg-danger text-white">
                      {{course.studentsList.filter(s => s.totalGrade < 70).length}} </span>
                        <p class="small mb-0">Reprobados</p>
                  </div>
                </div>

                <div class="d-none d-md-flex align-items-center gap-3">
                  <div class="d-flex gap-4">
                    <div class="text-center">
                      <span class="badge bg-dark text-white">
                        {{ course.students }}
                      </span>
                      <p class="small mb-0">Estudiantes</p>
                    </div>
                    <div v-if="course.isOpen" class="text-center">
                      <span class="badge bg-success text-white">
                        {{course.studentsList.filter(s => s.totalGrade >= 70).length}}
                      </span>
                      <p class="small mb-0">Aprobados</p>
                    </div>
                    <div v-if="course.isOpen" class="text-center">
                      <span class="badge bg-danger text-white">
                        {{course.studentsList.filter(s => s.totalGrade < 70).length}} </span>
                          <p class="small mb-0">Reprobados</p>
                    </div>
                  </div>
                  <i class="fas fa-chevron-down transition-all ms-2" :class="{ 'rotate-180': course.isOpen }"></i>

                </div>         
              </div>

              <transition name="expand">
                <div v-if="course.isOpen" class="p-2 p-md-3 card-body bg-gray-100 border-bottom">
                  <!-- Student Loop - Wrapped in scrollable container if many students -->
                  <div class="student-list-container" style="max-height: 400px; overflow-y: auto;">
                    <div v-if="course.studentsList.length === 0" class="text-center py-4">
                      <p class="text-muted">No hay estudiantes inscritos en este curso</p>
                    </div>
                    <div v-else v-for="student in course.studentsList" :key="student.id" class="mb-2 student-card">
                      <div
                        class="p-2 px-3 px-md-4 card-header cursor-pointer d-flex justify-content-between align-items-center"
                        @click="toggleStudent(course.id, student.id)"
                        :class="student.isOpen ? 'bg-gray-200 text-dark' : 'bg-white'">
                        <div>
                          <h6 class="mb-0 text-xs">{{ student.nombre }}</h6>
                          <p class="text-xs mb-0">{{ student.email }}</p>
                        </div>
                        <div class="d-flex align-items-center">
                          <span class="badge me-2" :class="{
                            'bg-success': student.totalGrade >= 70,
                            'bg-danger': student.totalGrade < 70,
                            'text-dark': student.isOpen && student.totalGrade >= 70
                          }">
                            {{ student.totalGrade }}%
                          </span>
                          <i class="fas fa-chevron-down ms-3 transition-all"
                            :class="{ 'rotate-180': student.isOpen }"></i>
                        </div>
                      </div>

                      <transition name="expand">
                        <div v-if="student.isOpen" class="p-2 p-md-3 pt-md-0 card-body bg-white">
                          <div class="table-responsive">
                            <table class="table align-items-center mb-0">
                              <thead>
                                <tr>
                                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Quiz
                                  </th>
                                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                                    Respuestas</th>
                                  <th
                                    class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                                    Calificación</th>
                                  <th
                                    class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                                    Estado</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="quiz in student.quizzes" :key="quiz.id">
                                  <td>
                                    <p class="text-xs font-weight-bold mb-0">{{ quiz.name }}</p>
                                  </td>
                                  <td>
                                    <p class="text-xs font-weight-bold mb-0">
                                      {{ quiz.correct }}/{{ quiz.total }} correctas
                                    </p>
                                  </td>
                                  <td class="align-middle text-center">
                                    <span class="badge badge-sm" :class="{
                                      'bg-success': quiz.grade >= 70,
                                      'bg-danger': quiz.grade < 70
                                    }">
                                      {{ quiz.grade }}
                                    </span>
                                  </td>
                                  <td class="align-middle text-center">
                                    <span class="text-xs">{{ quiz.submitted }}</span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </transition>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

.rotate-180 {
  transform: rotate(180deg);
}

/* Custom scrollbar styling */
.course-list-container::-webkit-scrollbar,
.student-list-container::-webkit-scrollbar {
  width: 8px;
}

.course-list-container::-webkit-scrollbar-track,
.student-list-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.course-list-container::-webkit-scrollbar-thumb,
.student-list-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.course-list-container::-webkit-scrollbar-thumb:hover,
.student-list-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Enhanced visual effects */
.card-header {
  transition: all 0.3s ease;
}

.course .card-header:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.category-badge {
  color: #344767 !important;
  background-color: #e0e7ff !important;
  padding: 0.25rem 0.5rem;
  margin-bottom: 10px;
}
</style>